import { useMemo } from 'react';
import styled from 'styled-components';

import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import { Box } from 'components/Box';
import { PrimaryButton } from 'components/Button';
import {
  InputAffixVariant,
  InputStyleVariant,
} from 'components/Input/Input.types';
import { Link } from 'components/Link';
import { ListingTypeSelect } from 'components/Listing/ListingTypeSelect';
import { LocationInput } from 'components/LocationInput/LocationInput';
import { UncontrolledSearchQueryInputContainer } from 'containers/UncontrolledSearchQueryInputContainer';
import { mainEverythingSearchRouteReference } from 'modules/main/main.routes';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors, cssBreakpoints } from 'theme/theme';
import { HOME_PAGE_SLUGS } from 'utils/constants/general/homePageSlugs';
import { scrollToTop } from 'utils/scroll';

const SearchInputsForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin: 0 auto;
  padding: 0;
  font-size: 18px;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    max-width: 400px;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    max-width: none;
  }
`;

type DisplayedType = ListingType | StaffTypeFilter;

type Props = {
  selectedType: DisplayedType;
  listingTypes: Array<DisplayedType>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearch: (...args: Array<any>) => any;
  initialQuery: string;
  query: string;
  redirectParam?: string;
  searchLocation: SearchLocation | null | undefined;
  searchLocationDisabled?: boolean;
  setListingType: (arg0: ListingType) => void;
  setQuery: (arg0: string) => void;
  onLocationChange: (searchLocation: SearchLocation | null | undefined) => void;
  search: (arg0: string) => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickLogin?: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickSignup?: (...args: Array<any>) => any;
  showLogin?: boolean;
  clearSearchInput: () => void;
  prefixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
};

export function SearchInputs({
  selectedType,
  listingTypes,
  onSearch,
  initialQuery,
  query,
  redirectParam,
  searchLocation,
  searchLocationDisabled,
  setListingType,
  setQuery,
  onLocationChange,
  search,
  clickLogin,
  clickSignup,
  showLogin,
  clearSearchInput,
  prefixVariant,
  styleVariant,
}: Props) {
  const { isEnvironmentLoaded } = useUserEnvironment();

  const locationInput = useMemo(
    () => (
      <LocationInput
        affixVariant={prefixVariant}
        styleVariant={styleVariant}
        disabled={searchLocationDisabled}
        hasRemoteOptions
        showLocationIcon
        showClearButton
        placeholder={getText('Everywhere')}
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        locationText={searchLocation && searchLocation.text}
        onChange={onLocationChange}
      />
    ),
    [
      prefixVariant,
      searchLocation,
      searchLocationDisabled,
      onLocationChange,
      styleVariant,
    ],
  );

  return (
    <SearchInputsForm
      method="GET"
      action={mainEverythingSearchRouteReference.with({
        lang: CURRENT_LOCALE,
        segment: HOME_PAGE_SLUGS.ALL[CURRENT_LOCALE],
      })}
      onSubmit={(e) => {
        e.preventDefault();
        search(query);

        if (onSearch) {
          onSearch();
        }
      }}
      data-qa-id="search-inputs"
    >
      <Box component="label" display="block" flex="1">
        <Box display="none">{getText('Select listing type')}</Box>
        <ListingTypeSelect
          value={selectedType}
          onChange={setListingType}
          listingTypes={listingTypes}
          variant="outline-blue"
        />
      </Box>

      <Box height="100%" width="100%" flex={[null, null, null, '2']}>
        <UncontrolledSearchQueryInputContainer
          placeholder={getText('Search by keyword, skill, or interest')}
          xsPlaceholder={getText('Keyword, skill, or interest')}
          initialQuery={initialQuery}
          onChange={setQuery}
          onClearQuery={clearSearchInput}
          presentation="light-border"
          id="search-query"
          qaId="search-input"
        />
      </Box>

      <Box flex="1">{locationInput}</Box>

      <Box
        flex="0 0 auto"
        height="100%"
        display={['block', null, null, 'flex']}
        mt="16px"
      >
        <PrimaryButton
          icon={{ type: 'normalized', name: 'search' }}
          type="submit"
          data-qa-id="search-button"
          dataReady={isEnvironmentLoaded}
          onClick={scrollToTop}
          size="large"
          fullWidth
        >
          {getText('Search')}
        </PrimaryButton>

        {redirectParam && showLogin && (
          <Box
            flex="1 1 auto"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mt={[24, null, null, 0]}
          >
            <Link href={`/login?${redirectParam}`} onClick={clickLogin}>
              {getText('Log In')}
            </Link>

            <Box mx="8px" color={colors.elementGrey}>
              |
            </Box>

            <Link href={`/signup?${redirectParam}`} onClick={clickSignup}>
              {getText('Sign up')}
            </Link>
          </Box>
        )}
      </Box>
    </SearchInputsForm>
  );
}
